<template>
  <main class="relative min-h-screen overflow-hidden bg-brand-surface-muted text-brand-ink">
    <div class="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-brand-surface to-transparent" aria-hidden="true" />
    <div class="pointer-events-none absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full bg-brand-fill/5 blur-3xl" aria-hidden="true" />

    <div class="relative mx-auto flex min-h-screen w-full max-w-7xl flex-col px-6 py-8 md:px-10">
      <header class="flex items-center justify-between">
        <BrandLogo
          :name="branding.companyLogo"
          :label="branding.portalName"
          class="h-20 w-auto text-brand-ink"
        />
        <label class="sr-only" for="portal-login-locale">{{ t("app.locale.switcherLabel") }}</label>
        <select
          id="portal-login-locale"
          class="h-10 rounded-none border border-brand-line bg-brand-surface px-3 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-ink"
          :aria-label="t('app.locale.switcherLabel')"
          :value="locale"
          @change="setLocale(($event.target as HTMLSelectElement).value as Locale)"
        >
          <option v-for="option in availableLocales" :key="option" :value="option">
            {{ t(`app.locale.${option}`) }}
          </option>
        </select>
      </header>

      <div class="flex flex-1 items-center justify-center py-16">
        <section class="w-full max-w-md">
          <div v-if="branding.productLogo" class="mb-8 flex flex-col items-center gap-3 text-center">
            <BrandLogo
              :name="branding.productLogo"
              :label="branding.portalName"
              class="h-16 w-auto text-brand-ink"
            />
          </div>

          <div class="border border-brand-line bg-brand-surface p-8 shadow-[0_1px_0_0_rgba(1,60,78,0.04)] md:p-10">
            <div class="space-y-3 text-center">
              <h1 class="text-3xl font-bold tracking-tight">{{ t("login.title") }}</h1>
              <p class="mx-auto max-w-sm text-sm leading-6 text-brand-muted">
                {{ t("login.description") }}
              </p>
            </div>

            <div class="mt-8 space-y-3">
              <input
                v-if="loginProviders.includes('demo')"
                v-model="demoPassword"
                type="password"
                autocomplete="current-password"
                class="h-12 w-full rounded-none border border-brand-line bg-brand-surface px-4 text-sm text-brand-ink"
                :placeholder="t('login.demoPassword')"
                :aria-label="t('login.demoPassword')"
              />
              <Button
                v-for="provider in loginProviders"
                :key="provider"
                type="button"
                class="h-12 w-full rounded-none border-0 bg-brand-fill text-[12px] font-bold uppercase tracking-[0.14em] text-white shadow-none transition-colors hover:bg-brand-fill-hover"
                :disabled="busy"
                @click="onLogin(provider)"
              >
                <Loader2 v-if="busyProvider === provider" class="size-4 animate-spin" />
                <LogIn v-else class="size-4" />
                {{ busyProvider === provider ? t("login.busy") : loginLabel(provider) }}
              </Button>

              <p v-if="auth.state.error" class="flex items-start gap-2 pt-2 text-sm text-destructive">
                <AlertCircle class="mt-0.5 size-4 shrink-0" />
                <span>{{ auth.state.error }}</span>
              </p>
            </div>

            <div class="mt-8 flex items-center justify-center gap-2 border-t border-brand-line pt-6 text-xs text-brand-muted">
              <ShieldCheck class="size-4 text-brand-ink/70" />
              <span>{{ t("login.productDescription") }}</span>
            </div>
          </div>

          <p v-if="branding.supportEmail" class="mt-6 text-center text-xs text-brand-muted">
            {{ t("login.help") }}
            <a
              class="font-semibold text-brand-ink underline-offset-4 hover:underline"
              :href="`mailto:${branding.supportEmail}`"
            >
              {{ t("login.support") }}
            </a>
          </p>
        </section>
      </div>

      <footer class="flex flex-col items-center justify-between gap-2 pb-2 text-[11px] text-brand-muted md:flex-row">
        <span>&copy; {{ new Date().getFullYear() }} {{ branding.copyright }}</span>
        <span class="font-bold uppercase tracking-[0.2em]">{{ branding.portalName }}</span>
      </footer>
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { AlertCircle, Loader2, LogIn, ShieldCheck } from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useI18n, type Locale } from "@/lib/i18n";
import type { AuthProviderName } from "@token/shared";
import BrandLogo from "@/components/brand/BrandLogo.vue";
import { useConfig } from "@/lib/config";
import { getBranding } from "@/lib/branding";

const auth = useAuth();
const router = useRouter();
const route = useRoute();
const busyProvider = ref<AuthProviderName | null>(null);
const demoPassword = ref("");
const autoLoginStarted = ref(false);
const { availableLocales, locale, setLocale, t } = useI18n();
const branding = getBranding(useConfig().branding);

const busy = computed(() => busyProvider.value !== null);
const loginProviders = computed(() => (auth.providers?.length ? auth.providers : [auth.name as AuthProviderName]));
const canAutoLoginWithAuth0 = computed(() => loginProviders.value.includes("auth0"));

function loginLabel(provider: AuthProviderName) {
  if (provider === "auth0") return t("login.auth0");
  if (provider === "google") return t("login.google");
  if (provider === "demo") return t("login.demo");
  return t("login.mock");
}

async function onLogin(provider: AuthProviderName) {
  busyProvider.value = provider;
  try {
    await auth.login(provider, undefined, provider === "demo" ? { password: demoPassword.value } : undefined);
  } finally {
    busyProvider.value = null;
  }
}

watch(
  () => auth.state.status,
  (status) => {
    if (status === "authenticated") {
      const redirect = typeof route.query.redirect === "string" ? route.query.redirect : "/";
      router.replace(redirect);
    }
  },
);

watch(
  [() => route.query.auth0_auto, () => auth.state.status, canAutoLoginWithAuth0],
  ([autoLogin, status, supportsAuth0]) => {
    if (autoLogin !== "1" || status !== "unauthenticated" || !supportsAuth0 || autoLoginStarted.value) return;
    autoLoginStarted.value = true;
    void onLogin("auth0");
  },
  { immediate: true },
);
</script>
